
import useVuelidate from "@vuelidate/core";
import { email } from "@vuelidate/validators";
import { computed, defineComponent, ref, watch, PropType } from "vue";
import FormErrors from "@/forms/FormErrors.vue";
import {
  RoleAssigneeType,
  UserRoleType,
  UserType,
  OrganizationType,
} from "@/graphql/types";
import { useQuery, useResult } from "@vue/apollo-composable";
import gql from "graphql-tag";
import RoleOnBehalfOfPicker from "./pickers/RoleOnBehalfOfPicker.vue";
import UserRoleProfilePicker from "./pickers/UserRoleProfilePicker.vue";
import { VDPopupListboxClass } from "@/components/pickers/VDPopupListbox";
import useRemoveUserRole from "@/views/User/mutations/useRemoveUserRole";
import useAddUserRoleOnBehalfOf from "./mutaions/useAddUserRoleOnBehalfOf";
import useToastService from "@/composables/useToastService";
import Error from "@/components/Error.vue";

const GET_USER_BY_EMAIL = gql`
  query GetUsersByEmail($email: String!, $excludeOrganizationId: ID!) {
    usersByEmail(email: $email, excludeOrganizationId: $excludeOrganizationId) {
      userId
      email
      firstname
      lastname
      organization {
        name
        vat
      }
      roles {
        roleId
        organizationId
        applicationId
        roleName
        organizationName
        applicationName
      }
    }
  }
`;

export default defineComponent({
  props: {
    organization: {
      type: Object as PropType<OrganizationType | null>,
      default: null,
    },
  },
  components: {
    FormErrors,
    RoleOnBehalfOfPicker,
    UserRoleProfilePicker,
    Error,
  },
  setup(props, ctx) {
    const toastService = useToastService();
    const roleOnBehalfOfPickerPicker = ref<VDPopupListboxClass>();
    const userRoleProfilePicker = ref<VDPopupListboxClass>();
    const isVisible = ref(false);
    const emailField = ref<string>("");
    const userIdAssigningRole = ref<string | null>(null);
    const userAssigningRole = ref<UserType>({ roles: [] });
    const usersByEmail = ref<any[]>();
    const v$ = useVuelidate(useValidations(), { emailField: emailField });
    const queryVariables = ref({});
    const queryOptions = ref();
    const selectedUser = ref();

    const addUserRoleOnBehalfOf = useAddUserRoleOnBehalfOf();
    const removeUserRole = useRemoveUserRole();

    queryOptions.value = {
      enabled: false,
      fetchPolicy: "network-only",
    };

    watch(emailField, (newValue) => {
      queryVariables.value = {
        email: newValue,
        excludeOrganizationId: props.organization?.organizationId,
      };
      queryOptions.value = {
        enabled: !v$.value.$invalid && emailField.value !== "",
        fetchPolicy: "network-only",
      };
    });

    function reset() {
      emailField.value = "";
      userIdAssigningRole.value = null;
      userAssigningRole.value = {};
      v$.value.$reset();
      resultGetUserOfEmail.value = null;
      usersByEmail.value = undefined;
      selectedUser.value = null;
    }

    const {
      result: resultGetUserOfEmail,
      loading: loadingGetUserOfEmail,
      onError: onErrorGetUserOfEmail,
    } = useQuery(GET_USER_BY_EMAIL, queryVariables, queryOptions);

    onErrorGetUserOfEmail((error) => {
      toastService.show({
        severity: "error",
        title: "Error",
        message: error.message,
      });
    });

    addUserRoleOnBehalfOf.onDone(() => {
      toastService.show({
        severity: "success",
        title: "Tildel rolle",
        message: "Rolle tildelt med succes",
      });
      ctx.emit("reload-tables");
    });

    removeUserRole.onDone(() => {
      toastService.show({
        severity: "success",
        title: "Fjern rolle",
        message: "Rolle fjernet med succes",
      });
      ctx.emit("reload-tables");
    });

    function handleInput() {
      v$.value.$touch();
    }

    watch(
      () => useResult(resultGetUserOfEmail).value,
      (usersByEmailData) => {
        if (usersByEmailData) {
          setupUserDataForSelectionList(usersByEmailData);
        }
      },
      { immediate: true }
    );

    function setupUserDataForSelectionList(usersByEmailData: any) {
      userIdAssigningRole.value = null;
      userAssigningRole.value = {};
      selectedUser.value = null;

      usersByEmail.value = [...usersByEmailData];
      if (usersByEmailData.length === 1) {
        selectedUser.value = usersByEmail.value[0];
        onUserSelected(new Event("change"));
      }
    }

    function onRemovedRole(role: RoleAssigneeType) {
      function remove() {
        userAssigningRole.value.roles =
          userAssigningRole.value.roles?.filter(
            (element) =>
              element?.roleId != role.roleId ||
              element?.organizationId != role.organizationId
          ) ?? [];

        usersByEmail.value = usersByEmail.value?.map((user) => {
          if (user.userId === userIdAssigningRole.value) {
            return {
              ...user,
              roles: userAssigningRole.value.roles,
            };
          }
          return user;
        });
      }

      removeUserRole
        .call(
          role.roleId ?? "",
          userAssigningRole.value.userId ?? "",
          role.organizationId ?? ""
        )
        .then((value) => {
          if (value != null) {
            remove();
          }
        });
    }

    function onAddedRole(role: UserRoleType) {
      function add() {
        userAssigningRole.value.roles = [
          ...(userAssigningRole.value.roles ?? []),
          {
            roleId: role.roleId,
            roleName: role.roleName,
            organizationId: props?.organization?.organizationId,
            organizationName: props?.organization?.name,
            applicationId: role?.applicationId,
            applicationName: role?.applicationName,
          },
        ];

        usersByEmail.value = usersByEmail.value?.map((user) => {
          if (user.userId === userIdAssigningRole.value) {
            return {
              ...user,
              roles: userAssigningRole.value.roles,
            };
          }
          return user;
        });
      }

      addUserRoleOnBehalfOf
        .call(
          userIdAssigningRole.value ?? "",
          role.roleId ?? "",
          props?.organization?.organizationId ?? ""
        )
        .then((value) => {
          if (value != null) {
            add();
          }
        });
    }

    function onProfilesSelected(data: {
      profile: any;
      availableRoles: any[];
      unavailableRoles: any[];
    }) {
      const { profile, availableRoles, unavailableRoles } = data;

      if (availableRoles.length === 0 && unavailableRoles.length === 0) {
        toastService.show({
          severity: "info",
          title: "Ingen roller",
          message: `Profilen "${profile.name}" har ingen roller at tildele`,
        });
        return;
      }

      if (availableRoles.length === 0) {
        toastService.show({
          severity: "warn",
          title: "Ingen tilgængelige roller",
          message: `Profilen "${profile.name}" indeholder ${unavailableRoles.length} roller, men ingen af dem er tildelt organisationen som "på vegne af" roller.`,
        });
        return;
      }

      // Filter out roles that are already assigned to the user
      const rolesToAdd = availableRoles.filter((role) => {
        return !userAssigningRole.value.roles?.some(
          (userRole) => userRole?.roleId === role.roleId
        );
      });

      if (rolesToAdd.length === 0) {
        let message = `Alle tilgængelige roller fra profilen "${profile.name}" er allerede tildelt profil.`;
        if (unavailableRoles.length > 0) {
          message += ` ${unavailableRoles.length} roller kunne ikke tildeles da de ikke er tildelt organisationen som "på vegne af" roller.`;
        }
        toastService.show({
          severity: "info",
          title: "Ingen nye roller",
          message: message,
        });
        return;
      }

      // Process roles sequentially
      processRoleProfileQueue(profile.name, rolesToAdd, unavailableRoles);
    }

    async function processRoleProfileQueue(
      profileName: string,
      rolesToAdd: any[],
      unavailableRoles: any[]
    ) {
      let addedCount = 0;
      let errorCount = 0;

      for (const role of rolesToAdd) {
        try {
          await addUserRoleOnBehalfOf.call(
            userIdAssigningRole.value ?? "",
            role.roleId,
            props?.organization?.organizationId ?? ""
          );

          // Add to local array on success
          userAssigningRole.value.roles = [
            ...(userAssigningRole.value.roles ?? []),
            {
              roleId: role.roleId,
              roleName: role.name,
              organizationId: props?.organization?.organizationId,
              organizationName: props?.organization?.name,
              applicationId: role?.applicationId,
              applicationName: role?.application?.name,
            },
          ];
          addedCount++;
        } catch (error) {
          errorCount++;
          console.error("Failed to add role:", role.name, error);
        }
      }

      // Show result message
      let message = "";
      if (addedCount > 0) {
        message = `${addedCount} roller fra profilen "${profileName}" er tilføjet.`;
      }

      if (errorCount > 0) {
        message += ` ${errorCount} roller fejlede.`;
      }

      if (unavailableRoles.length > 0) {
        message += ` ${unavailableRoles.length} roller kunne ikke tilføjes da de ikke er tildelt organisationen som "på vegne af" roller.`;
      }

      const severity =
        errorCount > 0
          ? "warn"
          : unavailableRoles.length > 0
          ? "warn"
          : "success";
      const title =
        addedCount > 0 ? "Rolleprofil tilføjet" : "Fejl ved tilføjelse";

      toastService.show({
        severity: severity,
        title: title,
        message: message,
      });
    }

    function onUserSelected(event: Event) {
      userIdAssigningRole.value = selectedUser.value.userId;
      userAssigningRole.value = JSON.parse(JSON.stringify(selectedUser.value));
    }

    return {
      isVisible,
      v$,
      loadingGetUserOfEmail,
      show: () => {
        isVisible.value = true;
        reset();
      },
      hide: () => {
        isVisible.value = false;
        reset();
      },
      emailField,
      handleInput,
      roleOnBehalfOfPickerPicker,
      userRoleProfilePicker,
      addRemoveLoading: computed(
        () =>
          userIdAssigningRole.value === null ||
          loadingGetUserOfEmail.value ||
          addUserRoleOnBehalfOf.loading.value ||
          removeUserRole.loading.value
      ),
      userAssigningRole,
      usersByEmail,
      onRemovedRole,
      userIdAssigningRole,
      onAddedRole,
      onProfilesSelected,
      onUserSelected,
      selectedUser,
    };
  },
});

function useValidations() {
  return {
    emailField: { email },
  };
}
